import { useEffect, useState } from "react";
import { DEFAULT_LOCATION_FORM } from "../adminConstants";
import {
  formatCommissionRateInput,
  generatePinCode,
  mapAdminLocation,
  mapAdminPartner,
  mapAdminPartnerDetail,
} from "../adminUtils";
import {
  createAdminLocation,
  deleteAdminLocation,
  getAdminLocations,
  updateAdminLocation,
} from "../../../api/adminLocations";
import {
  createAdminPartner,
  deleteAdminPartner,
  getAdminPartnerDetail,
  getAdminPartners,
  resetAdminPartnerPin,
  revealAdminPartnerPin,
  updateAdminPartner,
} from "../../../api/adminPartners";

const useAdminPartnersLocations = ({ showFeedback, refreshAudit, setActiveTab }) => {
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnersError, setPartnersError] = useState("");
  const [expandedPartnerId, setExpandedPartnerId] = useState(null);
  const [partnerDetailsById, setPartnerDetailsById] = useState({});
  const [partnerDetailsLoadingId, setPartnerDetailsLoadingId] = useState(null);
  const [partnerCommissionEdits, setPartnerCommissionEdits] = useState({});
  const [partnerPinLoadingId, setPartnerPinLoadingId] = useState(null);
  const [partnerFormOpen, setPartnerFormOpen] = useState(false);
  const [partnerEditingId, setPartnerEditingId] = useState(null);
  const [partnerForm, setPartnerForm] = useState(() => ({
    name: "",
    locationId: "",
    pin: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    commissionRate: "15",
  }));
  const [isSavingPartner, setIsSavingPartner] = useState(false);
  const [revealedPins, setRevealedPins] = useState({});
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState("");
  const [locationFormOpen, setLocationFormOpen] = useState(false);
  const [locationEditingId, setLocationEditingId] = useState(null);
  const [locationForm, setLocationForm] = useState(() => ({ ...DEFAULT_LOCATION_FORM }));
  const [isSavingLocation, setIsSavingLocation] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadPartners = async () => {
      setPartnersLoading(true);
      setPartnersError("");
      try {
        const payload = await getAdminPartners();
        if (cancelled) return;
        const items = Array.isArray(payload) ? payload.map(mapAdminPartner) : [];
        setPartners(items);
      } catch (error) {
        if (cancelled) return;
        setPartnersError(error?.message || "Failed to load partners.");
        setPartners([]);
      } finally {
        if (!cancelled) setPartnersLoading(false);
      }
    };

    loadPartners();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadLocations = async () => {
      setLocationsLoading(true);
      setLocationsError("");
      try {
        const payload = await getAdminLocations();
        if (cancelled) return;
        const items = Array.isArray(payload) ? payload.map(mapAdminLocation) : [];
        setLocations(items);
      } catch (error) {
        if (cancelled) return;
        setLocationsError(error?.message || "Failed to load locations.");
        setLocations([]);
      } finally {
        if (!cancelled) setLocationsLoading(false);
      }
    };

    loadLocations();

    return () => {
      cancelled = true;
    };
  }, []);

  const openPartnerForm = () => {
    setPartnerEditingId(null);
    setPartnerForm({
      name: "",
      locationId: locations[0]?.id ?? "",
      pin: "",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
      commissionRate: "15",
    });
    setPartnerFormOpen(true);
    setActiveTab("loc-partners");
  };

  const handlePartnerFormChange = (field) => (event) => {
    const rawValue = event.target.value;
    if (field === "pin") {
      const sanitized = rawValue.replace(/\D/g, "").slice(0, 6);
      setPartnerForm((prev) => ({ ...prev, pin: sanitized }));
      return;
    }
    if (field === "commissionRate") {
      const sanitized = rawValue.replace(/[^0-9.]/g, "");
      const parts = sanitized.split(".");
      const normalized =
        parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("")}` : sanitized;
      setPartnerForm((prev) => ({ ...prev, commissionRate: normalized }));
      return;
    }
    setPartnerForm((prev) => ({ ...prev, [field]: rawValue }));
  };

  const handlePartnerCommissionChange = (partnerId, rawValue) => {
    const sanitized = rawValue.replace(/[^0-9.]/g, "");
    const parts = sanitized.split(".");
    const normalized = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("")}` : sanitized;
    setPartnerCommissionEdits((prev) => ({ ...prev, [partnerId]: normalized }));
  };

  const handlePartnerCommissionSave = async (partner) => {
    if (!partner) return;
    const fallbackRate =
      partnerDetailsById[partner.id]?.commissionRate ?? partner.commissionRate;
    const rawValue =
      partnerCommissionEdits[partner.id] ?? formatCommissionRateInput(fallbackRate);
    const trimmedValue = rawValue == null ? "" : String(rawValue).trim();
    const rateValue = trimmedValue === "" ? null : Number.parseFloat(trimmedValue);
    if (rateValue === null || !Number.isFinite(rateValue)) {
      showFeedback("Commission rate must be a number.", "warning");
      return;
    }
    if (rateValue < 0 || rateValue > 100) {
      showFeedback("Commission rate must be between 0 and 100.", "warning");
      return;
    }
    try {
      const updated = await updateAdminPartner(partner.id, {
        commissionRate: rateValue / 100,
      });
      const mapped = mapAdminPartner(updated);
      setPartners((prev) => prev.map((item) => (item.id === partner.id ? mapped : item)));
      setPartnerDetailsById((prev) => {
        const existing = prev[partner.id];
        if (!existing) return prev;
        const next = {
          ...existing,
          commissionRate: mapped.commissionRate,
        };
        if (Number.isFinite(existing.revenueTotal)) {
          next.commissionOwed = existing.revenueTotal * mapped.commissionRate;
        }
        return { ...prev, [partner.id]: next };
      });
      setPartnerCommissionEdits((prev) => ({
        ...prev,
        [partner.id]: formatCommissionRateInput(mapped.commissionRate),
      }));
      await refreshAudit({ silent: true });
      showFeedback(`Commission rate updated for ${partner.name}.`, "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to update commission rate.", "warning");
    }
  };

  const handleGeneratePartnerPin = () => {
    const pin = generatePinCode();
    setPartnerForm((prev) => ({ ...prev, pin }));
    showFeedback(`Generated PIN: ${pin}`, "info");
  };

  const handlePartnerSave = async (event) => {
    event.preventDefault();
    const name = partnerForm.name.trim();
    const pin = partnerForm.pin.trim();
    const locationId = partnerForm.locationId;
    const contactName = partnerForm.contactName.trim();
    const contactEmail = partnerForm.contactEmail.trim();
    const contactPhone = partnerForm.contactPhone.trim();
    const commissionRateInput = partnerForm.commissionRate.trim();
    const commissionRatePercent =
      commissionRateInput === "" ? null : Number.parseFloat(commissionRateInput);
    if (commissionRatePercent !== null && !Number.isFinite(commissionRatePercent)) {
      showFeedback("Commission rate must be a number.", "warning");
      return;
    }
    if (commissionRatePercent !== null && (commissionRatePercent < 0 || commissionRatePercent > 100)) {
      showFeedback("Commission rate must be between 0 and 100.", "warning");
      return;
    }
    const commissionRate =
      commissionRatePercent === null ? null : commissionRatePercent / 100;
    if (!name) {
      showFeedback("Partner name is required.", "warning");
      return;
    }
    if (!locationId) {
      showFeedback("Select a partner location.", "warning");
      return;
    }
    if (!partnerEditingId && !pin) {
      showFeedback("PIN is required for new partners.", "warning");
      return;
    }
    if (pin && !/^\d{4,6}$/.test(pin)) {
      showFeedback("PIN must be 4 to 6 digits.", "warning");
      return;
    }

    setIsSavingPartner(true);
    try {
      if (partnerEditingId) {
        const updated = await updateAdminPartner(partnerEditingId, {
          name,
          locationId,
          pin: pin || null,
          contactName,
          contactEmail,
          contactPhone,
          commissionRate,
        });
        const mapped = mapAdminPartner(updated);
        setPartners((prev) =>
          prev.map((partner) => (partner.id === partnerEditingId ? mapped : partner))
        );
        setPartnerFormOpen(false);
        setPartnerEditingId(null);
        setPartnerForm({
          name: "",
          locationId: "",
          pin: "",
          contactName: "",
          contactEmail: "",
          contactPhone: "",
          commissionRate: "15",
        });
        await refreshAudit({ silent: true });
        showFeedback(`Partner ${name} updated.`, "success");
      } else {
        const created = await createAdminPartner({
          name,
          locationId,
          pin,
          contactName,
          contactEmail,
          contactPhone,
          commissionRate,
        });
        setPartners((prev) => [mapAdminPartner(created), ...prev]);
        setPartnerFormOpen(false);
        setPartnerForm({
          name: "",
          locationId: "",
          pin: "",
          contactName: "",
          contactEmail: "",
          contactPhone: "",
          commissionRate: "15",
        });
        await refreshAudit({ silent: true });
        showFeedback(`Partner ${name} created.`, "success");
      }
    } catch (error) {
      showFeedback(error?.message || "Failed to save partner.", "warning");
    } finally {
      setIsSavingPartner(false);
    }
  };

  const handlePartnerEdit = (partner) => {
    setPartnerEditingId(partner.id);
    setPartnerForm({
      name: partner.name,
      locationId: partner.locationId,
      pin: "",
      contactName: partner.contactName || "",
      contactEmail: partner.contactEmail || "",
      contactPhone: partner.contactPhone || "",
      commissionRate: formatCommissionRateInput(partner.commissionRate),
    });
    setPartnerFormOpen(true);
    setActiveTab("loc-partners");
  };

  const applyPartnerDetail = (partnerId, detail) => {
    setPartnerDetailsById((prev) => ({ ...prev, [partnerId]: detail }));
  };

  const loadPartnerPin = async (partner) => {
    if (!partner || revealedPins[partner.id]) return;
    setPartnerPinLoadingId(partner.id);
    try {
      const response = await revealAdminPartnerPin(partner.id);
      const pin = response?.pin;
      if (pin) {
        setRevealedPins((prev) => ({ ...prev, [partner.id]: pin }));
      } else {
        showFeedback("PIN cannot be revealed. Reset to generate a new PIN.", "warning");
      }
    } catch (error) {
      showFeedback(error?.message || "Failed to reveal PIN.", "warning");
    } finally {
      setPartnerPinLoadingId((current) => (current === partner.id ? null : current));
    }
  };

  const handlePartnerDetailsToggle = (partner) => {
    if (!partner) return;
    const partnerId = partner.id;
    const willExpand = expandedPartnerId !== partnerId;
    setExpandedPartnerId((prev) => (prev === partnerId ? null : partnerId));
    if (willExpand && !revealedPins[partnerId]) {
      loadPartnerPin(partner);
    }
    if (willExpand && !partnerDetailsById[partnerId]) {
      setPartnerDetailsLoadingId(partnerId);
      getAdminPartnerDetail(partnerId)
        .then((payload) => {
          const detail = mapAdminPartnerDetail(payload);
          applyPartnerDetail(partnerId, detail);
          setPartnerCommissionEdits((prev) => ({
            ...prev,
            [partnerId]: formatCommissionRateInput(detail.commissionRate),
          }));
        })
        .catch((error) => {
          showFeedback(error?.message || "Failed to load partner details.", "warning");
        })
        .finally(() => {
          setPartnerDetailsLoadingId((current) =>
            current === partnerId ? null : current
          );
        });
    }
  };

  const handlePartnerResetPin = async (partner) => {
    if (!partner) return;
    setPartnerPinLoadingId(partner.id);
    try {
      const response = await resetAdminPartnerPin(partner.id);
      const pin = response?.pin;
      if (pin) {
        setRevealedPins((prev) => ({ ...prev, [partner.id]: pin }));
        showFeedback(`New PIN for ${partner.name}: ${pin}`, "success");
      } else {
        showFeedback("PIN reset, but no PIN returned.", "warning");
      }
      setPartners((prev) =>
        prev.map((item) =>
          item.id === partner.id ? { ...item, updatedUtc: new Date().toISOString() } : item
        )
      );
      await refreshAudit({ silent: true });
    } catch (error) {
      showFeedback(error?.message || "Failed to reset PIN.", "warning");
    } finally {
      setPartnerPinLoadingId((current) => (current === partner.id ? null : current));
    }
  };

  const handlePartnerAccessToggle = async (partner) => {
    if (!partner) return;
    const nextActive = !partner.isActive;
    try {
      const updated = await updateAdminPartner(partner.id, { isActive: nextActive });
      const mapped = mapAdminPartner(updated);
      setPartners((prev) =>
        prev.map((item) => (item.id === partner.id ? mapped : item))
      );
      showFeedback(
        `${partner.name} ${nextActive ? "activated" : "deactivated"}.`,
        "info"
      );
      await refreshAudit({ silent: true });
    } catch (error) {
      showFeedback(error?.message || "Failed to update partner.", "warning");
    }
  };

  const handlePartnerDelete = async (partner) => {
    if (!partner) return;
    if (!window.confirm(`Delete partner ${partner.name}? This cannot be undone.`)) {
      return;
    }
    try {
      await deleteAdminPartner(partner.id);
      setPartners((prev) => prev.filter((item) => item.id !== partner.id));
      setRevealedPins((prev) => {
        const next = { ...prev };
        delete next[partner.id];
        return next;
      });
      setPartnerDetailsById((prev) => {
        if (!prev[partner.id]) return prev;
        const next = { ...prev };
        delete next[partner.id];
        return next;
      });
      setPartnerCommissionEdits((prev) => {
        if (!prev[partner.id]) return prev;
        const next = { ...prev };
        delete next[partner.id];
        return next;
      });
      if (expandedPartnerId === partner.id) {
        setExpandedPartnerId(null);
      }
      if (partnerEditingId === partner.id) {
        setPartnerEditingId(null);
        setPartnerFormOpen(false);
        setPartnerForm({
          name: "",
          locationId: "",
          pin: "",
          contactName: "",
          contactEmail: "",
          contactPhone: "",
          commissionRate: "15",
        });
      }
      await refreshAudit({ silent: true });
      showFeedback(`Partner ${partner.name} deleted.`, "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to delete partner.", "warning");
    }
  };

  const openLocationForm = () => {
    setLocationEditingId(null);
    setLocationForm({ ...DEFAULT_LOCATION_FORM });
    setLocationFormOpen(true);
    setActiveTab("loc-partners");
  };

  const handleLocationFormChange = (field) => (event) => {
    const { type, checked, value } = event.target;
    setLocationForm((prev) => ({
      ...prev,
      [field]:
        field === "isActive" ? value === "active" : type === "checkbox" ? checked : value,
    }));
  };

  const handleLocationSave = async (event) => {
    event.preventDefault();
    const name = locationForm.name.trim();
    const address = locationForm.address.trim();
    const postalCode = locationForm.postalCode.trim();
    const city = locationForm.city.trim();
    const country = locationForm.country.trim();
    const timezoneId = locationForm.timezoneId.trim();
    const operatingHours = locationForm.operatingHours.trim();
    const blockedDates = Array.isArray(locationForm.blockedDates)
      ? locationForm.blockedDates
      : [];
    const leadTimeRaw = `${locationForm.bookingLeadTimeHours ?? ""}`.trim();
    const bookingLeadTimeHours =
      leadTimeRaw === "" ? 0 : Number.parseInt(leadTimeRaw, 10);
    const cityFallback = city || address;
    const countryFallback = country || "Austria";
    const timezoneFallback = timezoneId || "Europe/Vienna";
    if (!name) {
      showFeedback("Location name is required.", "warning");
      return;
    }
    if (!address) {
      showFeedback("Address is required.", "warning");
      return;
    }
    if (!locationForm.supportsDayBookings && !locationForm.supportsNightBookings) {
      showFeedback("Select day, night, or both booking types.", "warning");
      return;
    }
    if (!Number.isFinite(bookingLeadTimeHours) || bookingLeadTimeHours < 0) {
      showFeedback("Booking time blocker must be zero or greater.", "warning");
      return;
    }

    const parseCoordinate = (value) => {
      const normalized = `${value ?? ""}`.trim().replace(",", ".");
      if (!normalized) return null;
      const parsed = Number.parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    };
    const parsedLatitude = parseCoordinate(locationForm.latitude);
    const parsedLongitude = parseCoordinate(locationForm.longitude);
    if ((parsedLatitude === null) !== (parsedLongitude === null)) {
      showFeedback("Please provide both latitude and longitude.", "warning");
      return;
    }

    setIsSavingLocation(true);
    try {
      const payload = {
        name,
        address,
        city: cityFallback,
        postalCode: postalCode || null,
        country: countryFallback,
        timezoneId: timezoneFallback,
        operatingHours: operatingHours || null,
        blockedDates,
        bookingLeadTimeHours,
        latitude: parsedLatitude,
        longitude: parsedLongitude,
        supportsDayBookings: locationForm.supportsDayBookings,
        supportsNightBookings: locationForm.supportsNightBookings,
        isActive: locationForm.isActive,
      };
      if (locationEditingId) {
        const updated = await updateAdminLocation(locationEditingId, payload);
        const mapped = mapAdminLocation(updated);
        setLocations((prev) =>
          prev.map((item) => (item.id === locationEditingId ? mapped : item))
        );
        showFeedback(`Location ${name} updated.`, "success");
        await refreshAudit({ silent: true });
      } else {
        const created = await createAdminLocation(payload);
        setLocations((prev) => [mapAdminLocation(created), ...prev]);
        showFeedback(`Location ${name} created.`, "success");
        await refreshAudit({ silent: true });
      }
      setLocationFormOpen(false);
      setLocationEditingId(null);
      setLocationForm({ ...DEFAULT_LOCATION_FORM });
    } catch (error) {
      showFeedback(error?.message || "Failed to save location.", "warning");
    } finally {
      setIsSavingLocation(false);
    }
  };

  const handleLocationEdit = (location) => {
    if (!location) return;
    setLocationEditingId(location.id);
    setLocationForm({
      name: location.name || "",
      address: location.address || "",
      city: location.city || "",
      postalCode: location.postalCode || "",
      country: location.country || "Austria",
      timezoneId: location.timezoneId || "Europe/Vienna",
      operatingHours: location.operatingHours || "",
      blockedDates: Array.isArray(location.blockedDates) ? location.blockedDates : [],
      bookingLeadTimeHours: `${location.bookingLeadTimeHours ?? 0}`,
      latitude: location.latitude != null ? String(location.latitude) : "",
      longitude: location.longitude != null ? String(location.longitude) : "",
      supportsDayBookings: location.supportsDayBookings ?? true,
      supportsNightBookings: location.supportsNightBookings ?? true,
      isActive: Boolean(location.isActive),
    });
    setLocationFormOpen(true);
    setActiveTab("loc-partners");
  };

  const handleLocationToggleActive = async (location) => {
    if (!location) return;
    const nextActive = !location.isActive;
    try {
      const updated = await updateAdminLocation(location.id, { isActive: nextActive });
      const mapped = mapAdminLocation(updated);
      setLocations((prev) => prev.map((item) => (item.id === location.id ? mapped : item)));
      showFeedback(
        `${location.name} ${nextActive ? "activated" : "deactivated"}.`,
        "info"
      );
      await refreshAudit({ silent: true });
    } catch (error) {
      showFeedback(error?.message || "Failed to update location.", "warning");
    }
  };

  const handleLocationDelete = async (location, onDeleted) => {
    if (!location) return;
    if (!window.confirm(`Delete location ${location.name}? This cannot be undone.`)) {
      return;
    }
    try {
      await deleteAdminLocation(location.id);
      setLocations((prev) => prev.filter((item) => item.id !== location.id));
      if (locationEditingId === location.id) {
        setLocationEditingId(null);
        setLocationFormOpen(false);
        setLocationForm({ ...DEFAULT_LOCATION_FORM });
      }
      if (onDeleted) {
        onDeleted(location.id);
      }
      await refreshAudit({ silent: true });
      showFeedback(`Location ${location.name} deleted.`, "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to delete location.", "warning");
    }
  };

  const handleLocationsRefresh = async () => {
    setLocationsLoading(true);
    setLocationsError("");
    try {
      const payload = await getAdminLocations();
      const items = Array.isArray(payload) ? payload.map(mapAdminLocation) : [];
      setLocations(items);
      showFeedback("Locations refreshed.", "info");
    } catch (error) {
      setLocationsError(error?.message || "Failed to load locations.");
      showFeedback(error?.message || "Failed to load locations.", "warning");
    } finally {
      setLocationsLoading(false);
    }
  };

  return {
    partners,
    setPartners,
    partnersLoading,
    partnersError,
    expandedPartnerId,
    setExpandedPartnerId,
    partnerDetailsById,
    partnerDetailsLoadingId,
    partnerCommissionEdits,
    partnerPinLoadingId,
    partnerFormOpen,
    setPartnerFormOpen,
    partnerEditingId,
    setPartnerEditingId,
    partnerForm,
    setPartnerForm,
    isSavingPartner,
    revealedPins,
    locations,
    setLocations,
    locationsLoading,
    locationsError,
    locationFormOpen,
    setLocationFormOpen,
    locationEditingId,
    setLocationEditingId,
    locationForm,
    setLocationForm,
    isSavingLocation,
    openPartnerForm,
    handlePartnerFormChange,
    handlePartnerCommissionChange,
    handlePartnerCommissionSave,
    handleGeneratePartnerPin,
    handlePartnerSave,
    handlePartnerEdit,
    handlePartnerDetailsToggle,
    handlePartnerResetPin,
    handlePartnerAccessToggle,
    handlePartnerDelete,
    openLocationForm,
    handleLocationFormChange,
    handleLocationSave,
    handleLocationEdit,
    handleLocationToggleActive,
    handleLocationDelete,
    handleLocationsRefresh,
  };
};

export default useAdminPartnersLocations;
